<template>
  <div class="container px-0 mb-2" id="trading-strategy-management">
    <CardRoot class="border-translucent">
      <CardHeader>
        <CardTitle>{{ $t("tradingCenter.title") }}</CardTitle>
      </CardHeader>
      <CardBody class="pt-0">
        <TabsContainer
          :tabs="tabs"
          @change:tab="setTab"
          :activeTab="activeTab || 'overview'"
        >
          <template #tab-overview>
            <StrategyOverviewTab
              :company="company"
              :registry-devices="registryDevicesStore.registryDevices"
            />
          </template>
          <template #tab-strategy-customisation>
            <StrategyCustomizationTab
              :company="company"
              :registry-devices="registryDevicesStore.registryDevices"
            />
          </template>
          <template #tab-forward-strategy>
            <ForwardSalesTabComponent :company />
          </template>
          <template #tab-issuance-reporting>
            <IssuanceReportingTab
              :company="company"
              :registry-devices="registryDevicesStore.registryDevices"
            />
          </template>
          <template #tab-devices>
            <RegistryDevicesSection
              v-if="company && registryDevicesStore.hasDevices"
              :company="company"
            />
            <PlaceholderCardComponent
              v-else
              :title="$t('dashboard.companyData.no_devices')"
              :subtitle="$t('dashboard.companyData.add_device_message')"
            >
            </PlaceholderCardComponent>
            <div class="mt-3">
              <button class="btn btn-primary" @click="goToRegisterNewDevice">
                <font-awesome-icon icon="plus" class="me-1" />
                {{ $t("dashboard.add_company_or_device") }}
              </button>
            </div>
          </template>
        </TabsContainer>
      </CardBody>
    </CardRoot>

    <!-- Loading overlay during portfolio operations -->
    <LoadingOverlayComponent v-if="productionStore.isLoadingPortfolios" />
  </div>
</template>
<script setup lang="ts">
import {
  CardBody,
  CardHeader,
  CardRoot,
  CardTitle,
} from "@/components/common/card";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import PlaceholderCardComponent from "@/components/common/PlaceholderCardComponent.vue";
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useRegistryDevicesStore } from "@/composables/useRegistryDevicesStore";
import { useTabs } from "@/composables/useTabs";
import ForwardSalesTabComponent from "@/dashboard/production_strategy/overview/forwards/ForwardSalesTabComponent.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import type { Company } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import IssuanceReportingTab from "./issuance-reporting/IssuanceReportingTab.vue";
import StrategyCustomizationTab from "./overview/StrategyCustomizationTab.vue";
import StrategyOverviewTab from "./overview/StrategyOverviewTab.vue";
import RegistryDevicesSection from "./RegistryDevicesSection.vue";

const props = defineProps<{
  company: Company;
  is_admin: boolean;
}>();

const { t } = useI18n();
const router = useRouter();
const registryDevicesStore = useRegistryDevicesStore();
const productionStore = useProductionStrategyStore();

const tabs = computed(() => {
  const availableTabs = [];

  availableTabs.push(
    {
      id: "overview",
      name: t("tradingCenter.tabs.overview"),
      icon: "chart-line",
    },
    {
      id: "strategy-customisation",
      name: t("tradingCenter.tabs.strategyCustomisation"),
      icon: "sliders-h",
    }
  );

  if (props.is_admin) {
    availableTabs.push({
      id: "issuance-reporting",
      name: t("tradingCenter.tabs.issuance_reporting.title"),
      icon: "file-invoice-dollar",
    });
  }

  if (props.company.feature_flags?.forward_sales_enabled) {
    availableTabs.push({
      id: "forward-strategy",
      name: t("production_strategy.forward_sales_overview.title"),
      icon: "calendar-alt",
    });
  }

  availableTabs.push({
    id: "devices",
    name: t("common.devices"),
    icon: "solar-panel",
  });

  return availableTabs;
});

const { activeTab, setTab } = useTabs("trading-strategy", {
  defaultTabId: "overview",
  tabs: tabs,
});

// Initialize portfolios and registry devices on component mount
onMounted(() => {
  if (props.company.id) {
    productionStore.refreshMultiCompanyData([props.company]);
    registryDevicesStore.fetchRegistryDevices(props.company.id);
  }
});

const goToRegisterNewDevice = () => {
  router.push({ name: "ProducerSignUp" });
};
</script>
